/* eslint-disable no-console */
import { BackButtonComponent } from '@/app/shared/back-button/back-button.component';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { DesyAngularModule } from 'desy-angular';
import { ProjectsService } from '@/app/services/ia-services/projects.service';
import { ProjectDetails } from '@/app/interfaces/project';
import { ReviewStatesComponent } from '@/app/shared/review-states/review-states.component';
import { ProjectIdentificationDataComponent } from '../components/project-identification-data/project-identification-data.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectFunctionalDataComponent } from '../components/project-functional-data/project-functional-data.component';
import { ProjectEconomicDataComponent } from '../components/project-economic-data/project-economic-data.component';
import { ProjectInputDataComponent } from '../components/project-input-data/project-input-data.component';
import { ProjectRiskDataComponent } from '../components/project-risk-data/project-risk-data.component';
import { ProjectAiSystemsComponent } from '../components/project-ai-systems/project-ai-systems.component';
import { ButtonsComponent } from '@/app/shared/buttons/buttons.component';
import { environment } from '@/environments/environment';
import { getProjectStateInfo } from '@/app/shared/mappers/project-state.mapper';

@Component({
  selector: 'app-project-details',
  imports: [
    CommonModule,
    BackButtonComponent,
    DesyAngularModule,
    ReviewStatesComponent,
    ProjectIdentificationDataComponent,
    ProjectFunctionalDataComponent,
    ProjectEconomicDataComponent,
    ProjectInputDataComponent,
    ProjectRiskDataComponent,
    ProjectAiSystemsComponent,
    ButtonsComponent,
  ],
  templateUrl: './project-details.component.html',
  styleUrls: ['./project-details.component.scss'],
})
export class ProjectDetailsComponent implements OnInit {
  private projectsService = inject(ProjectsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  id_project: string = '';
  userRoles: string[] = [];
  stateGroup: { group: string; state: string }[] = [];

  cardProject = signal<ProjectDetails | null>(null);
  loading = signal(true);
  projectDetail = signal<ProjectDetails>(null);
  stateServices = signal<{ text: string; state: string }[]>([]);
  stateTextFormat = signal('');
  colorBox = signal('');

  goToReview() {
    this.router.navigate(['revision'], {
      relativeTo: this.route,
      state: { project: this.projectDetail() },
    });
  }

  isCorrectionState(): boolean {
    return this.projectDetail()?.state?.toUpperCase() === 'CORRECTION';
  }

  goToCorrection(): void {
    this.router.navigate(['/catalogo-ia', 'proyectos', 'detalles', this.id_project, 'subsanar']);
  }

  private normalizeReviewState(state?: string | null): string {
    return (state ?? 'PENDING').toUpperCase();
  }

  private buildReviewersState(detail: ProjectDetails): string {
    const states = [
      detail.technicalReviewerState,
      detail.legalReviewerState,
      detail.functionalReviewerState,
      detail.dpdReviewerState,
    ].map((s) => this.normalizeReviewState(s));

    const allReviewed = states.every((state) => state !== 'PENDING');

    return allReviewed ? 'APPROVED' : 'PENDING';
  }

  private buildStateGroupFromBackend(detail: ProjectDetails): { group: string; state: string }[] {
    return [
      { group: 'support', state: this.normalizeReviewState(detail.supportGroupState) },
      { group: 'reviewers', state: this.buildReviewersState(detail) },
      { group: 'chief', state: this.normalizeReviewState(detail.serviceManagerState) },
    ];
  }

  canCurrentUserReview(): boolean {
    if (!this.stateGroup?.length) return false;

    const activeStage = this.stateGroup.find((stage) => stage.state === 'PENDING');
    if (!activeStage) return false;

    const detail = this.projectDetail();
    if (!detail) return false;

    switch (activeStage.group) {
      case 'support':
        return this.userRoles.includes('apoyo_OGIA');

      case 'reviewers': {
        const roleStateMap: Record<string, string | undefined> = {
          funcional_OGIA: detail.functionalReviewerState,
          juridico_OGIA: detail.legalReviewerState,
          tecnico_OGIA: detail.technicalReviewerState,
          dpd_OGIA: detail.dpdReviewerState,
        };
        return this.userRoles
          .filter((role) => role in roleStateMap)
          .some((role) => {
            const state = this.normalizeReviewState(roleStateMap[role]);
            return state === 'PENDING';
          });
      }
      case 'chief':
        return this.userRoles.includes('jefe_servicio_OGIA');

      default:
        return false;
    }
  }
  canCurrentUserViewReview(): boolean {
    const detail = this.projectDetail();
    if (!detail) {
      return false;
    }

    if (this.isCorrectionState()) {
      return this.isCurrentUserReviewRegistered(detail);
    }

    if (this.normalizeReviewState(detail.serviceManagerState) !== 'PENDING') {
      return false;
    }

    return this.isCurrentUserReviewRegistered(detail);
  }
  private isCurrentUserReviewRegistered(detail: ProjectDetails): boolean {
    const roleStateMap: Record<string, string | undefined> = {
      apoyo_OGIA: detail.supportGroupState,
      funcional_OGIA: detail.functionalReviewerState,
      juridico_OGIA: detail.legalReviewerState,
      tecnico_OGIA: detail.technicalReviewerState,
      dpd_OGIA: detail.dpdReviewerState,
      jefe_servicio_OGIA: detail.serviceManagerState,
    };

    return this.userRoles
      .filter((role) => role in roleStateMap)
      .some((role) => {
        const state = this.normalizeReviewState(roleStateMap[role]);
        return state === 'APPROVED' || state === 'CORRECTION';
      });
  }
  canCurrentUserAccessReview(): boolean {
    return this.canCurrentUserReview() || this.canCurrentUserViewReview();
  }
  get reviewButtonLabel(): string {
    if (!this.canCurrentUserReview()) {
      return 'Consultar revisión';
    }

    return 'Revisar proyecto';
  }
  get reviewStatesButtonLabel(): string {
    if (!this.canCurrentUserReview()) {
      return 'Consultar revisión';
    }

    return 'Revisar proyecto';
  }
  get activeStage(): string | undefined {
    if (this.isCorrectionState() && this.canCurrentUserViewReview()) {
      return 'chief';
    }

    return this.stateGroup.find((stage) => stage.state === 'PENDING')?.group;
  }
  canCurrentUserCorrect(): boolean {
    if (!this.isCorrectionState()) return false;
    if (this.userRoles.includes('jefe_servicio_OGIA')) return false;

    const allowedRoles = ['responsable_IA', 'jefe_consumidor'];

    return this.userRoles.some((role) => allowedRoles.includes(role));
  }
  async ngOnInit() {
    this.id_project = this.route.snapshot.paramMap.get('id') ?? '';
    const userData = JSON.parse(localStorage.getItem('userData')!);
    console.log('Roles:', userData.roles);
    const { data } = await this.projectsService.getProjectById(this.id_project, '', 'project');
    data.cards.forEach((card) => {
      card.buttons = [
        {
          label: card.state == null ? 'Consultar sistema de IA' : 'Consultar sistema desplegado',
          url:
            card.state == null
              ? '/catalogo-ia/sistemas-de-ia/detalles/' + card.id
              : '/catalogo-ia/sistemas-de-ia/despliegue/detalles/' + card.id,
        },
      ];
    });
    this.projectDetail.set(data);
    const projectState = getProjectStateInfo(this.projectDetail()?.state);
    this.stateTextFormat.set(projectState.label);
    this.colorBox.set(projectState.className);
    this.cardProject.set(data);
    this.stateGroup = this.buildStateGroupFromBackend(data);

    const projectOrgs = [data.codigo_organismo, data.orgCodeCreator].map((org) => environment.environment + org);
    console.log('Organizaciones del proyecto:', projectOrgs);
    this.userRoles = Object.entries(userData.roles ?? {})
      .filter(([, orgs]: [string, string[]]) => Array.isArray(orgs) && orgs.some((org) => projectOrgs.includes(org)))
      .map(([roleKey]) => roleKey as string);
    console.log('Rol del usuario para este proyecto:', this.userRoles);
    this.loading.set(false);
    console.log('Detalle del proyecto:', this.projectDetail());
  }
}
